import clsx from 'clsx'
import React from 'react'
import { Logger, NOOP_VOID } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import SearchIcon from '@mui/icons-material/Search'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'

import { Editor } from '@monaco-editor/react'

import { formatNumber } from 'src/utils/formatter-utils'
import {
    ActionsRowResponsive,
    buttonSx,
    chipSx,
    classToFComponent,
    dataGridRowHoverSx,
    dataGridSx,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    LifesHubConfirmDialog,
    LifesHubEmptyState,
    PanelCard,
    PanelCardBody,
    SkeletonBlock
} from 'src/utils/views'

import { MailTemplateOption, MailTemplateRow, MailTemplateScope } from '../scopes'
import { Banner, CardHead, FieldCaption, FieldGrid, SettingsStack } from 'src/utils/views/modules/admin-configurations'

const LOG = Logger.get('MailTemplate-VIEW')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        flexGrow: 1
    },
    input: {
        ...inputSx
    },
    filterRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 2.1fr) auto',
        gap: 12,
        alignItems: 'end',
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    filterActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        '@media (max-width: 720px)': {
            '& .MuiButton-root': { flexGrow: 1 }
        }
    },
    resultsCount: {
        marginTop: 15,
        color: 'var(--green-800)',
        fontWeight: 700
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: 18
    },
    grid: {
        ...dataGridSx,
        ...dataGridRowHoverSx,
        minHeight: 380,
        '& .MuiDataGrid-cell.actions': {
            padding: '0 6px'
        }
    },
    editorGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 12,
        height: 620,
        minWidth: 0,
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            height: 'auto'
        }
    },
    editorPanel: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 320,
        padding: 14,
        border: '1px solid var(--border)',
        borderRadius: 11,
        backgroundColor: '#fbfcfb',
        '@media (max-width: 1050px)': {
            height: 420
        }
    },
    editorPanelTitle: {
        margin: '0 0 8px',
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--ink)'
    },
    editorPanelBody: {
        flexGrow: 1,
        minHeight: 0,
        borderRadius: 9,
        overflow: 'hidden',
        border: '1px solid var(--border)'
    },
    previewFrame: {
        display: 'block',
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: '#fff'
    },
    chips: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        flexWrap: 'wrap'
    },
    cellTitle: {
        fontWeight: 750,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    cellDesc: {
        color: 'var(--muted)',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }
})

type MailTemplateListViewProps = {
    className?: string
    scope: MailTemplateScope
}

const templateOptionsLabelMap = new Map<MailTemplateOption, string>()

class MailTemplateListClass implements FCClassContext<MailTemplateListViewProps> {
    // :: emissors
    readonly emitFind = () => {
        const nameFilter = this.nameFilterInputRef.current?.value || ''
        this.scope.onFind(nameFilter).catch(LOG.caught)
    }

    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitEditClick = (id: GridRowId) => () => {
        this.scope.onEdit(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(Number(id)).catch(LOG.caught)
    }

    readonly emitOnYesResponse = () => {
        this.scope.onYesNoResponse(true).catch(LOG.caught)
    }

    readonly emitOnNoResponse = () => {
        this.scope.onYesNoResponse(false).catch(LOG.caught)
    }

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnContentChanged = (content: string) => {
        this.scope.onContentChanged(content).catch(LOG.caught)
    }

    readonly emitTemplateTypeChanged = (value: string) => {
        this.scope.onTemplateTypeChanged(value as MailTemplateOption).catch(LOG.caught)
    }

    // :: Fields
    scope!: MailTemplateScope
    sytles!: ReturnType<typeof useStyles>
    columns = static_build_columns(this.emitEditClick, this.emitDeleteClick)
    nameFilterInputRef!: React.RefObject<HTMLInputElement | null>

    // :: Methods

    onSyncState({ scope }: MailTemplateListViewProps) {
        initializeStatics()

        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: MailTemplateListViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nameFilterInputRef = React.useRef<HTMLInputElement>(null)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return (
                <div className={clsx(classes.view, className)}>
                    <SkeletonBlock sx={{ height: 118 }} />
                    <SkeletonBlock sx={{ height: 420 }} />
                </div>
            )
        }

        return (
            <div className={clsx(classes.view, className)}>
                {scope.tab === 'form' ? this.renderForm(scope) : this.renderList(scope)}

                <LifesHubConfirmDialog
                    open={!!scope.askYesOrNo}
                    title="Confirmar exclusão"
                    tone="danger"
                    message={scope.askYesOrNo}
                    confirmLabel="Excluir"
                    footerNote="A remoção é imediata e não pode ser desfeita por aqui."
                    onConfirm={this.emitOnYesResponse}
                    onCancel={this.emitOnNoResponse}
                />
            </div>
        )
    }

    renderList(scope: MailTemplateScope) {
        const { classes } = this.sytles

        return (
            <>
                <PanelCard elevation={0} sx={{ padding: '18px', flexShrink: 0 }}>
                    <div className={classes.filterRow}>
                        <Field>
                            <FieldLabel htmlFor="mailTemplateFilter">Buscar</FieldLabel>
                            <TextField
                                id="mailTemplateFilter"
                                className={classes.input}
                                fullWidth
                                defaultValue={scope.templateFilter}
                                inputRef={this.nameFilterInputRef}
                                placeholder="Buscar pelo nome do template"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 20, color: 'var(--muted)' }} />
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Field>
                        <div className={classes.filterActions}>
                            <Button sx={buttonSx()} startIcon={<SearchIcon />} onClick={this.emitFind}>
                                Buscar
                            </Button>
                            {this.renderNewButton()}
                        </div>
                    </div>
                    <div className={classes.resultsCount}>
                        {pluralize(scope.templates.length, 'template encontrado', 'templates encontrados')}
                    </div>
                </PanelCard>

                <PanelCard elevation={0} sx={{ flexGrow: 1, minHeight: 420 }}>
                    {scope.templates.length === 0 ? (
                        <LifesHubEmptyState
                            title="Nenhum template encontrado"
                            text={
                                scope.templateFilter
                                    ? 'Nenhum template corresponde ao texto buscado. Limpe a busca para ver a lista completa.'
                                    : 'Cadastre um modelo de mensagem para poder vinculá-lo aos eventos de conta na aba de e-mail.'
                            }
                            actions={this.renderNewButton()}
                        />
                    ) : (
                        <div className={classes.cardBody}>
                            <DataGrid
                                className={classes.grid}
                                rows={scope.templates}
                                columns={this.columns}
                                getRowId={static_getRowId}
                                disableRowSelectionOnClick
                                density="compact"
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                            />
                        </div>
                    )}
                </PanelCard>
            </>
        )
    }

    renderNewButton() {
        return (
            <Button sx={buttonSx('brand')} startIcon={<AddIcon />} onClick={this.emitNewClick}>
                Novo template
            </Button>
        )
    }

    renderForm(scope: MailTemplateScope) {
        const { classes } = this.sytles

        const isNew = !scope.templateId

        return (
            <SettingsStack>
                <PanelCard elevation={0}>
                    <CardHead>
                        <div>
                            <h3>{isNew ? 'Novo template de e-mail' : 'Editando template de e-mail'}</h3>
                            <p className="subtitle">Identificação da mensagem e evento em que ela é disparada.</p>
                        </div>
                        <ActionsRowResponsive>
                            <Button sx={buttonSx()} startIcon={<ArrowBackIcon />} onClick={this.emitOnClose}>
                                Voltar
                            </Button>
                            <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.emitOnSave}>
                                Salvar template
                            </Button>
                        </ActionsRowResponsive>
                    </CardHead>
                    <PanelCardBody>
                        <FieldGrid>
                            <Field>
                                <FieldLabel htmlFor="templateName">Nome do template</FieldLabel>
                                <TextField
                                    id="templateName"
                                    className={classes.input}
                                    fullWidth
                                    defaultValue={scope.templateName}
                                    onChange={(e) => (scope.templateName = e.target.value)}
                                />
                                <FieldHelp>É o nome que aparece na lista e na vinculação por evento.</FieldHelp>
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="templateType">Tipo do template</FieldLabel>
                                <TextField
                                    id="templateType"
                                    className={classes.input}
                                    select
                                    fullWidth
                                    defaultValue={scope.templateType}
                                    onChange={(evt) => this.emitTemplateTypeChanged(evt.target.value)}
                                >
                                    {[...templateOptionsLabelMap.keys()].map((tmp) => (
                                        <MenuItem key={tmp} value={tmp}>
                                            {templateOptionsLabelMap.get(tmp)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <FieldHelp>Define quais variáveis ficam disponíveis no conteúdo.</FieldHelp>
                            </Field>
                        </FieldGrid>

                        <Field>
                            <FieldLabel htmlFor="templateSubject">Assunto</FieldLabel>
                            <TextField
                                id="templateSubject"
                                className={classes.input}
                                fullWidth
                                defaultValue={scope.subject}
                                onChange={(e) => (scope.subject = e.target.value)}
                            />
                            <FieldHelp>Aparece na caixa de entrada antes de a mensagem ser aberta.</FieldHelp>
                        </Field>
                    </PanelCardBody>
                </PanelCard>

                <PanelCard elevation={0}>
                    <CardHead>
                        <div>
                            <h3>Conteúdo</h3>
                            <p className="subtitle">O HTML à esquerda e a prévia do que o usuário recebe à direita.</p>
                        </div>
                    </CardHead>
                    <PanelCardBody>
                        {this.renderVariables(scope)}

                        <div className={classes.editorGrid}>
                            <div className={classes.editorPanel}>
                                <h4 className={classes.editorPanelTitle}>Código</h4>
                                <div className={classes.editorPanelBody}>
                                    <Editor
                                        height="100%"
                                        width="100%"
                                        defaultLanguage="html"
                                        theme="vs-dark"
                                        value={scope.content}
                                        onChange={(v) => this.emitOnContentChanged(v || '')}
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: 14,
                                            wordWrap: 'on'
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={classes.editorPanel}>
                                <h4 className={classes.editorPanelTitle}>Prévia</h4>
                                <div className={classes.editorPanelBody}>
                                    <iframe
                                        title="preview"
                                        className={classes.previewFrame}
                                        srcDoc={scope.previewHtml}
                                    />
                                </div>
                            </div>
                        </div>
                    </PanelCardBody>
                </PanelCard>
            </SettingsStack>
        )
    }

    renderVariables(scope: MailTemplateScope) {
        const { classes } = this.sytles

        const unknown = (scope.variables ?? []).filter((v) => !scope.availableVariables?.includes(v))

        return (
            <>
                <Field>
                    <FieldCaption>Variáveis disponíveis</FieldCaption>
                    <div className={classes.chips}>
                        {scope.availableVariables?.length ? (
                            scope.availableVariables.map((v) => (
                                <Chip
                                    key={v}
                                    size="small"
                                    label={`{{${v}}}`}
                                    sx={chipSx(scope.variables?.includes(v) ? 'green' : 'gray')}
                                />
                            ))
                        ) : (
                            <FieldHelp>Nenhuma variável para este tipo de template.</FieldHelp>
                        )}
                    </div>
                    <FieldHelp>Em verde, as que o conteúdo atual já utiliza.</FieldHelp>
                </Field>

                {unknown.length > 0 && (
                    <Banner tone="warning">
                        O conteúdo usa {unknown.map((v) => `{{${v}}}`).join(', ')}, que o tipo escolhido não substitui —
                        essas marcações chegarão ao usuário como texto.
                    </Banner>
                )}
            </>
        )
    }
}

export const MailTemplateListView = classToFComponent(MailTemplateListClass, React)
export default MailTemplateListView

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function static_build_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'name',
            headerName: 'Template',
            width: 260,
            renderCell({ value, row }) {
                const label = (value as string) ?? '—'
                const type = templateOptionsLabelMap.get(row.templateType as MailTemplateOption)

                return (
                    <div style={{ minWidth: 0, paddingTop: 2, paddingBottom: 2 }}>
                        <div style={{ fontWeight: 750, lineHeight: 1.25 }} title={label}>
                            {label}
                        </div>
                        <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.25 }}>{type ?? '—'}</div>
                    </div>
                )
            }
        },
        {
            field: 'templateType',
            headerName: 'Evento',
            width: 190,
            renderCell({ value }) {
                const label = templateOptionsLabelMap.get(value as MailTemplateOption)
                return label ? <Chip size="small" label={label} sx={chipSx('blue')} /> : <span>—</span>
            }
        },
        {
            field: 'subject',
            headerName: 'Assunto',
            flex: 1,
            minWidth: 260
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 96,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={
                            <Tooltip title="Editar template">
                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Editar template"
                        onClick={handleEditClick(id)}
                        style={{ color: 'var(--green-800)' }}
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={
                            <Tooltip title="Excluir template">
                                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Excluir template"
                        onClick={handleDeleteClick(id)}
                        style={{ color: 'var(--danger)' }}
                    />
                ]
            }
        }
    ]
}

function static_getRowId(row: MailTemplateRow) {
    return row.seq + ''
}

let initializeStatics = () => {
    templateOptionsLabelMap.set('welcome', 'Boas-vindas')
    templateOptionsLabelMap.set('password-recovery', 'Esqueci a senha')
    templateOptionsLabelMap.set('new-password-generated', 'Geração de nova senha')

    initializeStatics = NOOP_VOID
}
